/* eslint-disable @ies/prettier/prettier */
import { computed, Ref, ref } from '@vue/composition-api';
import {
  AD_TYPE,
  NormalCreative,
  PROMOTE_MUSIC_VALIDATE_MAP,
  PrecheckResult,
  FullNormalFormData,
  CampaignFormData,
  AdgroupFormData,
  EcommType,
  PromotionFlowType,
} from '@tt4b/ttam-enums';
import { i18n } from '@tt4b/ttam-i18n';
import { getImageUrl, isCreativeEmpty } from '@tt4b/ttam-utils';
import { isEmpty } from 'lodash-es';
import { useAllowlist, useCreativeIs, useSplitTestEnabled, useVideoExtStatus, useStore, NetStateEnum, useCreativeIos14Quota } from '@tt4b/ttam-hooks';
import { scopedComputed } from './../../../utils/scopeFn';

import LiveDefaultImg from '@tt4b/ttam-assets/src/images/creative/tiktok-live-default.png';

export interface UseMaxCreativeLimitParams {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
  isRealTimeLive?: Ref<boolean>;
}

export function useMaxCreativeLimit({
  campaignData,
  adgroupData,
  isRealTimeLive = ref(false),
}: UseMaxCreativeLimitParams) {
  const { isCloseLoopLSA20White, isCloseLoopLSA50White, isCloseLoopVSA50White, isOpenLoopVSA50White } = useAllowlist();
  const { isSplitTestEnabled } = useSplitTestEnabled({ campaignData, adgroupData });
  const { ios14Quota } = useCreativeIos14Quota();

  return computed(() => {
    const { ecommType } = campaignData.value;
    const { promotionFlowType } = adgroupData.value;
    const { creativeQuota } = ios14Quota.value ?? {};

    const maxMap = {
      [EcommType.CATALOG]: {
        [PromotionFlowType.STANDARD]: creativeQuota?.totalQuota > 0
          ? creativeQuota.totalQuota
          : (isOpenLoopVSA50White.value ? 50 : 20),
        [PromotionFlowType.CLA]: 1,
      },
      [EcommType.SHOP]: {
        [PromotionFlowType.STANDARD]: isCloseLoopVSA50White.value ? 50 : 20,
        [PromotionFlowType.LIVE]: (
          isRealTimeLive.value ? 1 :
          isCloseLoopLSA50White.value ? 50 :
          isCloseLoopLSA20White.value ? 20 : 10
        ),
        [PromotionFlowType.PSA]: 1,
      },
    };

    const maxCreativeLimit = maxMap?.[ecommType]?.[promotionFlowType] ?? 20;

    // split test 不能使用异步创编，数量太多会超时，故限制最大创编数20
    return isSplitTestEnabled.value ? Math.min(maxCreativeLimit, 20) : maxCreativeLimit;
  });
}

export function useMaxCreativeLimitHint() {
  const { hasIos14CreativeQuota, creativeQuota } = useCreativeIos14Quota();

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const disabledHint = computed(() => hasIos14CreativeQuota.value
    ? i18n.t('module_skan4_reach_creative_quota_limit_hint', { quotaTotal: creativeQuota.value?.totalQuota ?? 0 })
    : ''
  );

  return {
    disabledHint,
  };
}

export function usePropsCreativeFormattedList(options: {
  normalCreativeList: Ref<NormalCreative[]>;
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
  isProductSales: Ref<boolean>;
  isControlGroup?: Ref<boolean>;
  validateStatus?: Ref<boolean[]>;
  precheckResult?: Ref<PrecheckResult>;
  isLiveAds?: Ref<boolean>;
  existCreatives: Ref<number>;
  createMode: Ref<string>;
  copyLoading: Ref<boolean>;
  copyCreativeUuid: Ref<string>;
}) {
  const {
    normalCreativeList,
    isProductSales,
    isControlGroup,
    validateStatus = ref([]),
    precheckResult = ref({} as PrecheckResult),
    isLiveAds,
    existCreatives,
    createMode,
    campaignData,
    adgroupData,
    copyLoading,
    copyCreativeUuid,
  } = options;

  function generateEachFormatted(creative: NormalCreative, index: number) {
    const { isNative, isCarouselImage, isPullTTPostNativeAd, isRealTimeLive } = useCreativeIs({
      createMode,
      campaignFormData: campaignData,
      adgroupFormData: adgroupData,
      normalCreativeData: ref(creative),
    });

    const { getVideoExtInfoByUuid } = useVideoExtStatus();

    const isUseTitle = isProductSales.value || isCarouselImage.value || isPullTTPostNativeAd.value;

    const title = isUseTitle
      ? i18n.t('module_ad_list_material_title_text')
      : i18n.t('module_ad_list_material_title_name');

    const isCanUpdateTitleBase = computed(
      () => !isControlGroup.value && !isProductSales.value && !isCarouselImage.value && !isNative.value,
    );

    function getCreativeTitle(data: any, hasVideo: boolean): string {
      if (isUseTitle) {
        return isCarouselImage.value ? data?.titles[0] : data?.title || '';
      }
      // 复制or编辑接口数据回填时，有fileName字段，新建从素材库选择，则使用name字段
      if (hasVideo) {
        return data?.videoInfo?.name || data?.videoInfo?.fileName || '';
      } else {
        return data?.imageInfo?.[0]?.name || data?.imageInfo?.[0]?.fileName || '';
      }
    }
    const { adSource, creativeUuid } = creative;
    const { adType } = creative[adSource];
    const formData: Partial<FullNormalFormData> = creative[adSource][adType];

    const isEmptyCreative = isCreativeEmpty(creative);

    const hasVideo = isEmptyCreative ?
      false :
      [AD_TYPE.CATALOGUE_VIDEO.value, AD_TYPE.SINGLE_VIDEO.value].includes(adType);

    const hasImage = isRealTimeLive.value ?
      true :
      isEmptyCreative ?
        false :
        [AD_TYPE.SINGLE_IMAGE.value, AD_TYPE.SINGLE_VIDEO.value, AD_TYPE.CAROUSEL_IMAGE.value].includes(adType) || // 单图/单视频/多图轮播创意不能为空
        [AD_TYPE.CATALOGUE_IMAGE.value, AD_TYPE.CATALOGUE_VIDEO.value].includes(adType); // DPA创意认为始终有图片
    const hasVid = hasVideo && formData.videoInfo?.vid;
    const maxCreaiveLimit = useMaxCreativeLimit({
      campaignData,
      adgroupData,
    });
    /**
     * 禁止复制
     * 1. 创意列表已达上线
     * 2. 空创意
     * 3. 异步渲染没有vid不能复制
     */
    const disableCopy = computed(() => {
      // 异步视频渲染，如果没有vid则不能复制
      const asyncRenderNotVideo = adType === AD_TYPE.SINGLE_VIDEO.value && !hasVid;
      return (
        normalCreativeList.value.length >= maxCreaiveLimit.value - existCreatives.value ||
        isEmptyCreative ||
        asyncRenderNotVideo ||
        isRealTimeLive.value
      );
    });

    /**
     * 当前创意是否正在被复制
     * 复制存在异步操作（如果），通过状态标识和禁用频繁执行复制
     */
    const isCopying = computed(() => copyLoading.value && creativeUuid === copyCreativeUuid.value);

    /** 是否展示音乐版权缺失的提示 */
    const isShowMusicWarning = computed(() => {
      if (isControlGroup.value) {
        return false;
      }
      const { withPromotionalMusic, musicAuthorizationStatus } = formData || {};

      return !withPromotionalMusic && musicAuthorizationStatus === PROMOTE_MUSIC_VALIDATE_MAP.AUTHORIZATION_MISSING;
    });

    /** 是否展示预校验的warning */
    const isShowPreCheckWarning = computed(() => {
      if (isControlGroup.value) {
        return false;
      }
      const materialResult = precheckResult.value?.materialResults?.[index];
      if (isEmpty(materialResult)) {
        return false;
      }

      const keys = ['videos', 'images', 'displayName'];

      return keys.some(key => materialResult[key]?.some(item => item.suggestion.length > 0));
    });

    const imageUrl = isRealTimeLive.value
      ? LiveDefaultImg
      : getImageUrl(formData, hasVideo, hasImage, isPullTTPostNativeAd.value);

    return {
      hasImage,
      hasVideo,
      title,
      creativeTitle: getCreativeTitle(formData, hasVideo),
      imageUrl,
      isCanUpdateTitle: hasVideo && isCanUpdateTitleBase.value,
      isShowValidateError: typeof validateStatus.value?.[index] === 'boolean' ? !validateStatus.value[index] : false,
      isShowMusicWarning: isShowMusicWarning.value,
      isShowPreCheckWarning: isShowPreCheckWarning.value,
      disableCopy: disableCopy.value,
      isCopying: isCopying.value,
      videoProcessConfig: getVideoExtInfoByUuid(creativeUuid) ?? {},
      creativeUuid,
    };
  }

  const formattedList = scopedComputed(() =>
    normalCreativeList.value?.map?.((item, index) => generateEachFormatted(item, index)),
  );

  return {
    formattedList,
  };
}
